import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { AlertTriangle, Bell, CheckCircle2, Filter, Download, Share2, Loader2, Sparkles, X } from 'lucide-react';
import { getPredictiveAlerts, acknowledgeAlert, getAlertStats, getMineralsList, generatePrediction, getAvailableMinerals, getClaudeStatus } from '../services/predictiveAlertsApi';

// Theme-aware primary color (use CSS variable so dark mode works)
const primaryColorClass = "text-[var(--color-primary)]";
const primaryBgClass = "bg-[var(--color-primary)]";

function SeverityBadge({ severity }) {
  const map = {
    RED: "bg-red-500/10 dark:bg-red-500/20 text-red-700 dark:text-red-300 border-red-500/30 dark:border-red-500/40",
    YELLOW: "bg-yellow-500/10 dark:bg-yellow-500/20 text-yellow-700 dark:text-yellow-300 border-yellow-500/30 dark:border-yellow-500/40",
    GREEN: "bg-green-500/10 dark:bg-green-500/20 text-green-700 dark:text-green-300 border-green-500/30 dark:border-green-500/40",
  };
  return (
    <span className={`px-2 py-0.5 text-xs border rounded ${map[severity]}`}>
      {severity}
    </span>
  );
}

function SummaryBar({ stats }) {
  const counts = {
    red: stats.critical || 0,
    yellow: stats.elevated || 0,
    green: stats.watchlist || 0,
  };

  return (
    <div className="grid grid-cols-3 gap-3 mb-6">
      <Card className="rounded-2xl shadow-sm">
        <CardContent className="p-4">
          <div className={`text-xs opacity-80 ${primaryColorClass}`}>Critical</div>
          <div className="text-3xl font-semibold text-gray-900 dark:text-gray-100">{counts.red}</div>
          <div className="text-[10px] opacity-60 text-gray-600 dark:text-gray-400">RED</div>
        </CardContent>
      </Card>
      <Card className="rounded-2xl shadow-sm">
        <CardContent className="p-4">
          <div className={`text-xs opacity-80 ${primaryColorClass}`}>Elevated</div>
          <div className="text-3xl font-semibold text-gray-900 dark:text-gray-100">{counts.yellow}</div>
          <div className="text-[10px] opacity-60 text-gray-600 dark:text-gray-400">YELLOW</div>
        </CardContent>
      </Card>
      <Card className="rounded-2xl shadow-sm">
        <CardContent className="p-4">
          <div className={`text-xs opacity-80 ${primaryColorClass}`}>Watchlist</div>
          <div className="text-3xl font-semibold text-gray-900 dark:text-gray-100">{counts.green}</div>
          <div className="text-[10px] opacity-60 text-gray-600 dark:text-gray-400">GREEN</div>
        </CardContent>
      </Card>
    </div>
  );
}

function AlertCard({ alert, onAck, onExport, onShare }) {
  const borderColor = alert.severity === 'RED' ? 'border-red-500' : 
                     alert.severity === 'YELLOW' ? 'border-yellow-500' : 'border-green-500';

  return (
    <Card className={`rounded-2xl shadow-sm border-l-4 ${borderColor}`}>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <SeverityBadge severity={alert.severity} />
            <Badge variant="outline">
              {alert.type.replaceAll('_',' ')}
            </Badge>
          </div>
          <div className="text-xs opacity-60 text-gray-600 dark:text-gray-400">
            {new Date(alert.timestamp).toLocaleString()}
          </div>
        </div>
        
        <div className="text-base font-semibold text-gray-900 dark:text-gray-100">{alert.title}</div>
        <div className="text-sm opacity-80 text-gray-700 dark:text-gray-300">{alert.description}</div>
        <div className="text-sm text-gray-700 dark:text-gray-300">
          <strong className="text-gray-900 dark:text-gray-100">Impact:</strong> {alert.impact}
        </div>
        <div className="text-sm text-gray-700 dark:text-gray-300">
          <strong className="text-gray-900 dark:text-gray-100">Recommendation:</strong> {alert.recommendation}
        </div>
        
        <div className="flex gap-2 pt-2">
          <Button 
            size="sm" 
            variant="secondary" 
            onClick={() => onAck(alert)}
            className="flex items-center gap-1"
          >
            <CheckCircle2 className="w-4 h-4"/>
            Acknowledge
          </Button>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => onExport(alert)}
            className="flex items-center gap-1"
          >
            <Download className="w-4 h-4"/>
            Export
          </Button>
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={() => onShare(alert)}
            className="flex items-center gap-1"
          >
            <Share2 className="w-4 h-4"/>
            Share
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function PredictiveAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState({ critical: 0, elevated: 0, watchlist: 0 });
  const [minerals, setMinerals] = useState([]);
  const [filters, setFilters] = useState({ severity: "ALL", mineral: "ALL" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedMineral, setSelectedMineral] = useState('');
  const [availableMinerals, setAvailableMinerals] = useState([]);
  const [claudeStatus, setClaudeStatus] = useState({ available: false, message: '' });

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch alerts, stats, minerals, Claude status, and available minerals in parallel
        const [alertsData, statsData, mineralsData, claudeStatusData, availableMineralsData] = await Promise.all([
          getPredictiveAlerts(filters),
          getAlertStats(),
          getMineralsList(),
          getClaudeStatus(),
          getAvailableMinerals()
        ]);
        
        setAlerts(alertsData);
        setStats(statsData);
        setMinerals(mineralsData);
        // Set Claude status - handle both success and error cases
        if (claudeStatusData && typeof claudeStatusData === 'object') {
          setClaudeStatus(claudeStatusData);
        } else {
          // If status check failed, set default but don't block the UI
          console.warn('Claude status check failed or returned unexpected format:', claudeStatusData);
          setClaudeStatus({ available: false, message: 'Status check failed' });
        }
        setAvailableMinerals(availableMineralsData);
        if (availableMineralsData.length > 0) {
          setSelectedMineral(availableMineralsData[0]);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load alerts data');
        // Keep empty state - no mock data fallback
        setAlerts([]);
        setStats({ critical: 0, elevated: 0, watchlist: 0 });
        setMinerals([]);
        // Set default Claude status on error
        setClaudeStatus({ available: false, message: 'Failed to check Claude status' });
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []); // Only run on mount

  // Refetch data when filters change
  useEffect(() => {
    const fetchFilteredData = async () => {
      try {
        setLoading(true);
        const alertsData = await getPredictiveAlerts(filters);
        setAlerts(alertsData);
      } catch (err) {
        console.error('Error fetching filtered data:', err);
        setError('Failed to load filtered alerts');
      } finally {
        setLoading(false);
      }
    };
    
    // Only refetch if not initial load
    if (!loading) {
      fetchFilteredData();
    }
  }, [filters]);

  const filtered = useMemo(() => alerts.filter(alert => (
    (filters.severity === "ALL" || alert.severity === filters.severity) &&
    (filters.mineral === "ALL" || alert.mineral === filters.mineral)
  )), [alerts, filters]);

  const onAck = async (alertData) => {
    try {
      await acknowledgeAlert(alertData.id);
      // Remove from local state
      setAlerts(prev => prev.filter(x => x.id !== alertData.id));
      // Update stats
      setStats(prev => ({
        ...prev,
        [alertData.severity === 'RED' ? 'critical' : 
         alertData.severity === 'YELLOW' ? 'elevated' : 'watchlist']: 
        prev[alertData.severity === 'RED' ? 'critical' : 
             alertData.severity === 'YELLOW' ? 'elevated' : 'watchlist'] - 1
      }));
    } catch (err) {
      console.error('Error acknowledging alert:', err);
      alert('Failed to acknowledge alert. Please try again.');
    }
  };

  const onExport = (alertData) => {
    const blob = new Blob([JSON.stringify(alertData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const aTag = document.createElement("a");
    aTag.href = url;
    aTag.download = `${alertData.id}.json`;
    aTag.click();
    URL.revokeObjectURL(url);
  };

  const onShare = async (alertData) => {
    const txt = `[${alertData.severity}] ${alertData.title} — ${alertData.mineral}\n${alertData.description}\nImpact: ${alertData.impact}\nRecommendation: ${alertData.recommendation}`;
    try {
      await navigator.clipboard.writeText(txt);
      alert("Copied alert summary to clipboard.");
    } catch {
      alert("Copy failed.");
    }
  };

  const handleGeneratePrediction = async () => {
    if (!selectedMineral) {
      alert('Please select a mineral');
      return;
    }

    try {
      setGenerating(true);
      setError(null);
      
      const result = await generatePrediction(selectedMineral);
      
      if (result.success) {
        // Refresh alerts and stats
        const [alertsData, statsData] = await Promise.all([
          getPredictiveAlerts(filters),
          getAlertStats()
        ]);
        
        setAlerts(alertsData);
        setStats(statsData);
        setShowGenerateModal(false);
        alert('Prediction generated successfully!');
      }
    } catch (err) {
      console.error('Error generating prediction:', err);
      setError(err.response?.data?.detail || 'Failed to generate prediction. Please check if ANTHROPIC_API_KEY is configured.');
      alert(err.response?.data?.detail || 'Failed to generate prediction. Please check if ANTHROPIC_API_KEY is configured.');
    } finally {
      setGenerating(false);
    }
  };

  const refreshData = async () => {
    try {
      setLoading(true);
      const [alertsData, statsData] = await Promise.all([
        getPredictiveAlerts(filters),
        getAlertStats()
      ]);
      setAlerts(alertsData);
      setStats(statsData);
    } catch (err) {
      console.error('Error refreshing data:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full p-6 space-y-6 bg-white dark:bg-gray-900 min-h-screen transition-colors duration-200">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className={`text-2xl font-semibold ${primaryColorClass}`}>
          Predictive Alerts
        </h1>
        <div className="flex items-center gap-2">
          <div className="flex gap-2">
            <select 
              className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" 
              value={filters.severity} 
              onChange={(e) => setFilters(f => ({...f, severity: e.target.value}))}
              disabled={loading}
            >
              <option value="ALL">All severities</option>
              <option value="RED">RED</option>
              <option value="YELLOW">YELLOW</option>
              <option value="GREEN">GREEN</option>
            </select>
            <select 
              className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" 
              value={filters.mineral} 
              onChange={(e) => setFilters(f => ({...f, mineral: e.target.value}))}
              disabled={loading}
            >
              <option value="ALL">All minerals</option>
              {minerals.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <Button 
            size="sm" 
            className={`flex items-center gap-1 ${primaryBgClass} hover:opacity-90 text-white border-0`}
            disabled={loading}
            onClick={refreshData}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Filter className="w-4 h-4"/>}
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
          <Button 
            size="sm" 
            className={`flex items-center gap-1 ${primaryBgClass} hover:opacity-90 text-white border-0`}
            disabled={loading || generating}
            onClick={() => {
              console.log('Claude Status:', claudeStatus);
              if (!claudeStatus?.available) {
                const message = claudeStatus?.message || 'Claude service is not available. Please check if ANTHROPIC_API_KEY is configured in api_keys_config.py';
                alert(message);
              }
              setShowGenerateModal(true);
            }}
            title={claudeStatus?.available ? 'Generate AI prediction' : (claudeStatus?.message || 'Claude service not available - click to see details')}
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin"/> : <Sparkles className="w-4 h-4"/>}
            Generate Prediction
            {!claudeStatus?.available && !loading && (
              <span className="ml-1 text-xs opacity-75">⚠️</span>
            )}
          </Button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500 dark:text-red-400"/>
            <span className="text-red-700 dark:text-red-200">{error}</span>
          </div>
        </div>
      )}

      {/* Summary Bar */}
      <SummaryBar stats={stats} />

      {/* Alert Cards Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className={`flex items-center gap-3 ${primaryColorClass}`}>
            <Loader2 className="w-6 h-6 animate-spin"/>
            <span>Loading alerts...</span>
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Bell className={`w-12 h-12 mx-auto mb-4 opacity-50 ${primaryColorClass}`}/>
            <h3 className={`text-lg font-medium mb-2 ${primaryColorClass}`}>No alerts found</h3>
            <p className="text-gray-500 dark:text-gray-400">No alerts match your current filters.</p>
          </div>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {filtered.map(alert => (
            <AlertCard 
              key={alert.id} 
              alert={alert} 
              onAck={onAck} 
              onExport={onExport} 
              onShare={onShare} 
            />
          ))}
        </div>
      )}

      {/* Generate Prediction Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className={`text-xl font-semibold ${primaryColorClass}`}>
                  Generate AI Prediction
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowGenerateModal(false)}
                  disabled={generating}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              {!claudeStatus.available && (
                <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400"/>
                    <span className="text-yellow-800 dark:text-yellow-200 text-sm">
                      Claude service not available. Please configure ANTHROPIC_API_KEY in api_keys_config.py
                    </span>
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <label className={`text-sm font-medium ${primaryColorClass}`}>
                  Select Mineral
                </label>
                <select
                  className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={selectedMineral}
                  onChange={(e) => setSelectedMineral(e.target.value)}
                  disabled={generating || !claudeStatus.available}
                >
                  {availableMinerals.length === 0 ? (
                    <option value="">No minerals available</option>
                  ) : (
                    availableMinerals.map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))
                  )}
                </select>
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button
                  className={`flex-1 ${primaryBgClass} hover:opacity-90 text-white border-0`}
                  onClick={handleGeneratePrediction}
                  disabled={!selectedMineral || generating || !claudeStatus.available}
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2"/>
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2"/>
                      Generate
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowGenerateModal(false)}
                  disabled={generating}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

    </div>
  );
}
